import * as React from 'react'

import {
    getPartialCategories,
    PartialCategory,
    PartialSection,
    resolveCategoryAndSection,
} from '@doist/help-center-utils'

import { Footer, Seo } from 'components'
import { BodyBackgroundWhite } from 'components/body-background-white'
import * as Sections from 'components/help/categories'
import { HelpHeader } from 'components/help/shared/Header/Header'
import {
    PrefooterSection,
    PrefooterVariant,
    PrefooterVariants,
} from 'components/help/shared/PrefooterSection/PrefooterSection'
import { helpCenterAllowedCategories } from 'config/constants'
import sample from 'lodash/sample'
import { GetServerSidePropsContext } from 'next'
import { useTranslation } from 'next-i18next'
import { serverSideTranslations } from 'next-i18next/serverSideTranslations'
import { getCommonServerSideProps } from 'services'
import { BannersClient as contentful } from 'services/contentful'
import { getContent } from 'services/help-center/content'
import { useLoggedUser } from 'services/hooks/useLoggedUser'
import { PageBanner } from 'types/contentful'

type CategoriesPageProps = {
    categories: PartialCategory[]
    category: PartialCategory
    section: PartialSection
    sectionProvided: boolean
    footerVariant: PrefooterVariant
    pageBanner?: PageBanner
}

export default function CategoriesPage({
    categories,
    category,
    section,
    sectionProvided,
    footerVariant,
    pageBanner,
}: CategoriesPageProps) {
    const { i18n } = useTranslation('helpcenter')
    const utm_campaign = 'helpcenter'
    const user = useLoggedUser()

    return (
        <>
            <Seo
                path={sectionProvided ? section.href : category.href}
                title={sectionProvided ? section.name : category.name}
                description={sectionProvided ? section.description : category.description}
                lang={i18n.language}
            />

            <BodyBackgroundWhite />

            <HelpHeader
                user={user}
                categories={categories}
                currentPath="/help"
                searchBarEnabled
                showContactUs
                pageBanner={pageBanner}
            />

            <main>
                <Sections.MainSection
                    categories={categories}
                    category={category}
                    section={section}
                />
                <PrefooterSection variant={footerVariant} />
            </main>

            <Footer utm_campaign={utm_campaign} backgroundColor={footerVariant.backgroundColor} />
        </>
    )
}

export async function getServerSideProps(ctx: GetServerSidePropsContext) {
    const { query } = ctx
    const { locale, ...commonProps } = await getCommonServerSideProps(ctx)

    const content = await getContent(locale)
    const validCategories = content.categories.filter((c) =>
        helpCenterAllowedCategories.includes(c.id),
    )
    const categories = getPartialCategories(validCategories)
    const [categoryId, sectionId] = query.slug as string[]
    const { category, section } = resolveCategoryAndSection(
        getPartialCategories(content.categories),
        categoryId,
        sectionId,
    )

    /** Build a randomly customized footer */
    const footerVariant = sample(PrefooterVariants) ?? PrefooterVariants[0]

    const currentPath = (sectionId ? section?.href : category?.href) ?? '/help'
    const pageBanner = await contentful.getPageBanner(currentPath, locale)

    return {
        props: {
            categories,
            category,
            section,
            footerVariant,
            /** Provide different SEO content if we share a single Category or a section */
            sectionProvided: sectionId !== undefined,
            ...(pageBanner && { pageBanner }),
            ...commonProps,
            ...(await serverSideTranslations(locale, ['common', 'helpcenter'])),
        },
    }
}
